import { useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { LoadingSpinner } from '@/components/common'
import { useAssistant, useAssistantDefaults, useCreateAssistant } from '@/hooks/api/useAssistants'
import { usePermissions } from '@/hooks/api'
import { useOnboardingData } from '@/hooks/api/useOnboarding'

// Shared form validation schema
import { createAssistantSchema, type AssistantFormData } from './schemas'

// Shared tab configuration
import {
  type TabId,
  getAssistantTabs,
  getVisibleTabs,
  tabHasErrors,
  DEFAULT_FORM_VALUES,
} from './config'

export function AssistantCreatePage() {
  const navigate = useNavigate()
  const { originalId, tab } = useParams<{ originalId?: string; tab?: string }>()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { canManage } = usePermissions()

  // Create schema with translated error messages
  const assistantSchema = useMemo(() => createAssistantSchema(t), [t])

  // Determine data source: cloning from existing assistant or from onboarding
  const isCloning = !!originalId
  const onboardingTaskId = searchParams.get('onboarding')
  const isFromOnboarding = !!onboardingTaskId

  // Scroll to top when page mounts (especially important for cloning)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  // Fetch original assistant when cloning
  const {
    data: originalAssistant,
    isLoading: isLoadingOriginal,
  } = useAssistant(isCloning ? originalId : undefined)

  // Fetch onboarding data when coming from onboarding flow
  const {
    data: onboardingData,
    isLoading: isLoadingOnboarding,
  } = useOnboardingData(onboardingTaskId)

  // Fetch default values (only if NOT cloning AND NOT from onboarding)
  const {
    data: defaultValues,
    isLoading: isLoadingDefaults,
  } = useAssistantDefaults(!isCloning && !isFromOnboarding)

  // Create mutation
  const createAssistant = useCreateAssistant()

  // Tabs configuration
  const tabs = useMemo(() => getAssistantTabs(t), [t])

  // Valid tab IDs for validation
  const validTabIds = useMemo(() => tabs.map((t) => t.id), [tabs])

  // Get active tab from URL, default to 'about'
  const activeTab = (tab as TabId) || 'about'

  // Build base path for tab navigation
  const basePath = isCloning ? `/assistants/create/clone/${originalId}` : '/assistants/create'

  // Build URL with preserved search params for tab navigation
  const buildTabUrl = (tabId: string) => {
    const params = searchParams.toString()
    return `${basePath}/${tabId}${params ? `?${params}` : ''}`
  }

  // Redirect to 'about' if invalid tab in URL
  useEffect(() => {
    if (tab && !validTabIds.includes(tab as TabId)) {
      navigate(buildTabUrl('about'), { replace: true })
    }
  }, [tab, validTabIds, basePath, navigate, searchParams])

  // Form setup
  const methods = useForm<AssistantFormData>({
    resolver: zodResolver(assistantSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  })

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting, errors },
  } = methods

  // Load data into form based on source: onboarding, cloning, or defaults
  useEffect(() => {
    // Determine which data source to use
    let dataToLoad = null
    let shouldExcludeUserSpecificFields = false

    if (isFromOnboarding && onboardingData) {
      // Data from onboarding flow - this is pre-generated assistant config
      dataToLoad = onboardingData
      shouldExcludeUserSpecificFields = true
    } else if (isCloning && originalAssistant) {
      // Cloning from an existing assistant
      dataToLoad = originalAssistant
      shouldExcludeUserSpecificFields = true
    } else if (defaultValues) {
      // Creating new assistant with defaults
      dataToLoad = defaultValues
      shouldExcludeUserSpecificFields = false
    }

    if (dataToLoad) {
      // When cloning or from onboarding, exclude these fields:
      // id, created_at, notification_user, caller_id, fallback_number, stripe_subscription, phone_number_id, customer_id
      const formData = {
        name: isCloning ? `${dataToLoad.name} (Copy)` : dataToLoad.name || '',
        // Exclude customer_id when cloning/onboarding - new assistant may be for a different customer
        customer_id: shouldExcludeUserSpecificFields ? undefined : dataToLoad.customer_id,
        voice_id: dataToLoad.voice_id,
        default_language: dataToLoad.default_language,
        additional_languages: dataToLoad.additional_languages || [],
        timezone: dataToLoad.timezone,
        greeting: dataToLoad.greeting,
        prompt: dataToLoad.prompt,
        post_call_prompt: dataToLoad.post_call_prompt,
        // Exclude notification_user when cloning/onboarding
        notification_user: shouldExcludeUserSpecificFields ? undefined : dataToLoad.notification_user,
        notification_outcomes: dataToLoad.notification_outcomes || [],
        ai_model: dataToLoad.ai_model,
        ai_deployment_id: dataToLoad.ai_deployment_id,
        // Exclude from_id (caller_id) when cloning/onboarding
        from_id: shouldExcludeUserSpecificFields ? undefined : dataToLoad.from_id,
        whatsapp_image: dataToLoad.whatsapp_image,
        is_active: dataToLoad.is_active !== false,
        config: dataToLoad.config || {},
        lexicon: dataToLoad.lexicon || {},
        prompt_scenarios: dataToLoad.prompt_scenarios || {},
        // Exclude phone_number_id when cloning/onboarding (unique constraint)
        phone_number_id: shouldExcludeUserSpecificFields ? undefined : dataToLoad.phone_number_id,
      }

      reset(formData)
    }
  }, [isCloning, isFromOnboarding, originalAssistant, onboardingData, defaultValues, reset])

  // Handlers
  const onSubmit = async (data: AssistantFormData) => {
    try {
      const result = await createAssistant.mutateAsync(data as Parameters<typeof createAssistant.mutateAsync>[0])
      toast.success(t('resources.assistants.create_success'))
      // Navigate to the newly created assistant's edit page
      navigate(`/assistants/${result.id}/about`)
    } catch (error: unknown) {
      const responseData = (error as { response?: { data?: { detail?: string; message?: string } } })?.response?.data
      const errorMessage = responseData?.message || responseData?.detail || t('resources.assistants.create_error')
      toast.error(errorMessage, {
        duration: 10000, // Show validation errors longer
      })
    }
  }

  const handleBack = () => {
    if (isDirty) {
      const confirmed = window.confirm(t('resources.assistants.unsaved_changes_warning'))
      if (!confirmed) return
    }
    navigate('/assistants')
  }

  // Permissions
  const isAdminOrPartner = canManage

  // Filter tabs based on permissions (isEditMode=false hides revisions tab)
  const visibleTabs = useMemo(
    () => getVisibleTabs(tabs, { isAdminOrPartner, isEditMode: false }),
    [tabs, isAdminOrPartner]
  )

  // Loading state - check all possible data sources
  const isLoading = isCloning
    ? isLoadingOriginal
    : isFromOnboarding
      ? isLoadingOnboarding
      : isLoadingDefaults

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex h-full min-w-0 flex-col overflow-x-hidden">
        {/* Header */}
        <PageHeader
          title={
            isCloning
              ? t('resources.assistants.clone')
              : isFromOnboarding
                ? t('resources.assistants.create_from_onboarding', { defaultValue: 'Review Your Assistant' })
                : t('resources.assistants.create')
          }
          subtitle={
            isFromOnboarding
              ? t('resources.assistants.create_from_onboarding_subtitle', {
                  defaultValue: 'Review and customize the AI assistant created from your business details',
                })
              : t('resources.assistants.create_subtitle', {
                  defaultValue: 'Configure a new AI assistant for your business',
                })
          }
          backButton={{
            label: t('resources.assistants.back_to_list'),
            onClick: handleBack,
          }}
        />

        {/* Tabs */}
        <div className="min-w-0 flex-1 overflow-hidden">
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={(key) => navigate(buildTabUrl(key as string), { replace: true })}
            className="flex h-full min-w-0 flex-col"
          >
            {/* Tab List */}
            <div className="border-b border-gray-200 bg-white px-4 sm:px-6 dark:border-gray-700 dark:bg-gray-900">
              <TabsList>
                {visibleTabs.map((tab) => {
                  const hasErrors = tabHasErrors(tab.id, errors)
                  return (
                    <TabsTrigger key={tab.id} id={tab.id}>
                      <span className="flex items-center gap-2">
                        {tab.label}
                        {tab.badge && (
                          <span className="rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-brand-700 dark:bg-brand-950 dark:text-brand-300">
                            {tab.badge}
                          </span>
                        )}
                        {hasErrors && (
                          <span className="flex h-2 w-2 rounded-full bg-red-500" title={t('app.validation.required')} />
                        )}
                      </span>
                    </TabsTrigger>
                  )
                })}
              </TabsList>
            </div>

            {/* Tab Panels */}
            <div className="min-w-0 flex-1 overflow-x-hidden">
              {visibleTabs.map((tab) => {
                const TabComponent = tab.component
                return (
                  <TabsContent key={tab.id} id={tab.id}>
                    <div className="w-full max-w-5xl px-4 py-4 sm:px-6 sm:py-6">
                      <TabComponent />
                    </div>
                  </TabsContent>
                )
              })}
            </div>
          </Tabs>
        </div>

        {/* Bottom Toolbar - matching v1 layout */}
        <div className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            {/* Left side: Save & close */}
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                className="flex-1 sm:flex-none"
                iconLeading={Save}
              >
                {t('resources.generic.save_and_close', { defaultValue: 'Save & close' })}
              </Button>
            </div>

            {/* Right side: empty for create page */}
            <div className="flex items-center gap-4" />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default AssistantCreatePage
